
import React, { useState } from 'react';
import { X, Search, School, Loader2, MapPin, Building, AlertCircle } from 'lucide-react';

export interface NeisSchoolData {
    SD_SCHUL_CODE: string;
    SCHUL_NM: string;
    SCHUL_KND_SC_NM: string;
    ATPT_OFCDC_SC_CODE: string;
    ATPT_OFCDC_SC_NM: string;
    ORG_RDNMA: string; // 도로명 주소
}

interface NeisSearchModalProps {
    onClose: () => void;
    onSelect: (school: NeisSchoolData) => void;
}

const NeisSearchModal: React.FC<NeisSearchModalProps> = ({ onClose, onSelect }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<NeisSchoolData[]>([]);
    const [error, setError] = useState<string | null>(null);

    const handleSearch = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!searchQuery.trim()) return;

        setLoading(true);
        setError(null);
        setResults([]);

        try {
            const apiKey = ''; // Public key used if empty, or add key if needed
            const url = `https://open.neis.go.kr/hub/schoolInfo?Type=json&pIndex=1&pSize=100&SCHUL_NM=${encodeURIComponent(searchQuery)}`;

            const response = await fetch(url);
            const data = await response.json();

            if (data.schoolInfo) {
                const schools = data.schoolInfo[1].row;
                setResults(schools);
            } else if (data.RESULT && data.RESULT.CODE === 'INFO-200') {
                setResults([]); // No results
            } else {
                setError('검색 중 오류가 발생했습니다.');
            }
        } catch (err) {
            console.error('NEIS API Error:', err);
            setError('학교 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-white relative z-10">
                    <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                        <School className="text-indigo-600" size={20} />
                        학교 검색
                    </h3>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-rose-500 transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="p-5 bg-slate-50/50 border-b border-slate-100">
                    <form onSubmit={handleSearch} className="relative">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="학교 이름을 입력하세요 (예: 서울고)"
                            className="w-full bg-white border-2 border-slate-200 rounded-xl pl-11 pr-4 py-3.5 text-sm font-bold focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all"
                            autoFocus
                        />
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <button
                            type="submit"
                            disabled={loading || !searchQuery.trim()}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-colors"
                        >
                            검색
                        </button>
                    </form>
                    <p className="text-[10px] text-slate-400 mt-2 pl-1 font-medium flex items-center gap-1">
                        <AlertCircle size={10} /> 정식 학교명을 입력하면 더 정확하게 검색됩니다.
                    </p>
                </div>

                {/* Results List */}
                <div className="flex-1 overflow-y-auto p-2 bg-slate-50 custom-scrollbar">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-3">
                            <Loader2 size={32} className="animate-spin text-indigo-500" />
                            <p className="text-xs font-bold">학교 정보를 찾고 있습니다...</p>
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center py-12 text-rose-500 gap-2">
                            <AlertCircle size={32} />
                            <p className="text-xs font-bold">{error}</p>
                        </div>
                    ) : results.length > 0 ? (
                        <div className="space-y-2 p-2">
                            {results.map((school) => (
                                <button
                                    key={school.SD_SCHUL_CODE}
                                    onClick={() => onSelect(school)}
                                    className="w-full bg-white p-4 rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-md hover:shadow-indigo-500/5 hover:-translate-y-0.5 text-left transition-all group"
                                >
                                    <div className="flex items-start justify-between mb-1">
                                        <h4 className="font-black text-slate-800 group-hover:text-indigo-700 transition-colors">
                                            {school.SCHUL_NM}
                                        </h4>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${school.SCHUL_KND_SC_NM === '초등학교' ? 'bg-yellow-50 text-yellow-600' :
                                                school.SCHUL_KND_SC_NM === '중학교' ? 'bg-emerald-50 text-emerald-600' :
                                                    school.SCHUL_KND_SC_NM === '고등학교' ? 'bg-blue-50 text-blue-600' :
                                                        'bg-slate-100 text-slate-500'
                                            }`}>
                                            {school.SCHUL_KND_SC_NM}
                                        </span>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-xs text-slate-500 flex items-center gap-1.5">
                                            <MapPin size={10} className="text-slate-300" />
                                            {school.ORG_RDNMA}
                                        </p>
                                        <p className="text-[10px] text-slate-400 flex items-center gap-1.5 pl-0.5">
                                            <Building size={9} className="text-slate-300" />
                                            {school.ATPT_OFCDC_SC_NM}
                                        </p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : searchQuery && !loading ? (
                        <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
                            <School size={32} className="opacity-20" />
                            <p className="text-xs font-bold">검색 결과가 없습니다.</p>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-300 gap-3">
                            <Search size={48} className="opacity-20" />
                            <p className="text-xs font-bold">학교 이름을 입력하여 검색하세요.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NeisSearchModal;
